/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import styled from "styled-components";
import * as React from "react";
import upgradeIcon from "components/shared/images/upgrade-icon.svg";
import { devices, fontFamilies, useIsMobile } from "components/shared/Styles";
import { ExpressCheckoutButtonPosition } from "constants/Analytics";
import { PurchaseExpressButton } from "components/PurchaseExpress";
import { CustomModal } from "components/shared/CustomModal";
import { PremiumIcon } from "components/shared/PremiumIcon";
import {
  EnhancePremiumFeatureList,
  ExpressPremiumFeatureList,
  ProductList,
  StudioFeatureList,
  ProductFeatureList,
} from "components/Plans/ProductList";
import { IconMode } from "components/Plans/IconComponent";
import { ProductFeature } from "components/Plans/ProductFeature";
import { CallToAction } from "components/Plans/Plan";
import iconCheckGreen from "components/Plans/images/check-green.svg";
import premiumPreview1x from "images/EnhanceSpeech/premium-video-shorter.png";
import premiumPreview2x from "images/EnhanceSpeech/premium-video-shorter@2x.png";
import { Icon } from "components/shared/Icon";
import { useCurrentUser } from "actions/CurrentUserActions";

const PremiumUserContentScrollView = styled.div`
  overflow-y: auto;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DefaultImageContainer = styled.img`
  border: 2px solid #dee1e6;
`;

export const UpgradeModal: React.FC<{
  onClose?: VoidFunction;
  open?: boolean;
  position: ExpressCheckoutButtonPosition;
  products?: ProductFeatureList[];
  productImage?: React.ReactNode;
}> = ({
  onClose = (): void => {},
  position,
  products = [
    EnhancePremiumFeatureList,
    StudioFeatureList,
    ExpressPremiumFeatureList,
  ],
  productImage = (
    <DefaultImageContainer
      srcSet={`${premiumPreview1x} 1x, ${premiumPreview2x} 2x`}
      alt=""
      aria-hidden="true"
    />
  ),
  open = false,
}) => {
  const isMobile = useIsMobile();
  const { currentUser } = useCurrentUser();

  if (currentUser?.hasPremiumAccess) {
    // TODO: Extract premium content to a separate component
    const premiumProducts = [
      EnhancePremiumFeatureList,
      StudioFeatureList,
      ExpressPremiumFeatureList,
    ];

    return (
      <CustomModal onClose={onClose} open={open} maxWidth={"1078px"}>
        <PremiumUserContentScrollView>
          <Title>
            {I18n.t("access_button.premium_tooltip")} <PremiumIcon />
          </Title>
          <Subtitle>{I18n.t("access_button.modal_subtitle")}</Subtitle>
          <ProductList
            products={premiumProducts}
            mode={IconMode.dark}
            compact={isMobile}
          />
        </PremiumUserContentScrollView>
      </CustomModal>
    );
  }

  return (
    <CustomModal onClose={onClose} open={open} maxWidth={"1078px"}>
      <Columns>
        <ImageContent>{productImage}</ImageContent>
        <TextContent>
          <Title>
            {I18n.t("plans.title")}
            &nbsp;
            <PremiumIcon />
          </Title>
          <ProductList
            products={products}
            mode={IconMode.dark}
            compact={isMobile}
          />
          <CallToAction data-testid="call-to-action">
            <PurchaseExpressButton
              color={"black"}
              position={position}
              onClick={onClose}
              onClosed={onClose}
              dataTestId="start-free-trial-button"
            >
              <Icon aria-hidden="true" src={upgradeIcon} />
              {I18n.t("plans.premium.button")}
            </PurchaseExpressButton>
            <ProductFeature
              image={iconCheckGreen}
              description={I18n.t("plans.premium.reminder")}
            />
          </CallToAction>
        </TextContent>
      </Columns>
    </CustomModal>
  );
};

const Title = styled.h1`
  font: normal normal 900 28px/30px ${fontFamilies};
  display: inline;
  gap: 8px;

  img {
    vertical-align: bottom;
  }
`;

const Subtitle = styled.div`
  font: normal normal normal 18px/24px ${fontFamilies};
`;

const ImageContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;

  img {
    object-fit: contain;
    max-width: 100%;
    border-radius: 12px;
  }

  ${devices.small}, ${devices.medium} {
    display: none;
  }
`;

const TextContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Columns = styled.div`
  display: flex;
  gap: 64px;
  overflow-y: scroll;
`;
